import { lumi } from '@/lib/lumi'

const recentHits = new Map<string, number>()
const DEDUPE_MS = 2500
export const ANALYTICS_TIMEZONE = 'America/Sao_Paulo'

export function toDateKey(date = new Date()) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: ANALYTICS_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date)
}

export function shiftDateKey(dateKey: string, deltaDays: number) {
  const [year, month, day] = dateKey.split('-').map(Number)
  const utc = new Date(Date.UTC(year, (month || 1) - 1, (day || 1) + deltaDays))
  const y = utc.getUTCFullYear()
  const m = String(utc.getUTCMonth() + 1).padStart(2, '0')
  const d = String(utc.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export function sumViewsByDateKey(
  records: Array<{ dateKey?: string; views?: number; homeViews?: number; articleViews?: number }>,
) {
  const map = new Map<string, number>()
  records.forEach(record => {
    if (!record?.dateKey) return
    const views = Number(record.views)
    const total = Number.isFinite(views)
      ? views
      : (Number(record.homeViews) || 0) + (Number(record.articleViews) || 0)
    map.set(record.dateKey, (map.get(record.dateKey) || 0) + total)
  })
  return map
}

function todayKey() {
  return toDateKey()
}

export async function trackPageView(kind: 'home' | 'article') {
  const dateKey = todayKey()
  const stampKey = `${kind}:${dateKey}:${window.location.pathname}`
  const now = Date.now()
  const last = recentHits.get(stampKey) || 0
  if (now - last < DEDUPE_MS) return
  recentHits.set(stampKey, now)

  try {
    const { list } = await lumi.entities.page_views.list({
      filter: { dateKey },
      limit: 1,
    })
    const existing = list?.[0] as {
      _id?: string
      views?: number
      homeViews?: number
      articleViews?: number
    } | undefined
    const nowIso = new Date().toISOString()

    if (existing?._id) {
      await lumi.entities.page_views.update(existing._id, {
        views: (existing.views || 0) + 1,
        homeViews: (existing.homeViews || 0) + (kind === 'home' ? 1 : 0),
        articleViews: (existing.articleViews || 0) + (kind === 'article' ? 1 : 0),
        updatedAt: nowIso,
      })
      return
    }

    await lumi.entities.page_views.create({
      dateKey,
      views: 1,
      homeViews: kind === 'home' ? 1 : 0,
      articleViews: kind === 'article' ? 1 : 0,
      creator: 'system',
      createdAt: nowIso,
      updatedAt: nowIso,
    })
  } catch (error) {
    console.error('Erro ao registar visualização:', error)
  }
}

export async function trackArticleView(articleId: string, currentViews?: number) {
  if (!articleId || typeof articleId !== 'string') return
  try {
    await lumi.entities.news_articles.update(articleId, {
      views: (currentViews || 0) + 1,
    })
  } catch (error) {
    console.error('Erro ao atualizar views do artigo:', error)
  }
}

export const BOOST_AMOUNT = 500

export async function boostDailyViews(amount = BOOST_AMOUNT) {
  const dateKey = todayKey()
  const nowIso = new Date().toISOString()
  const { list } = await lumi.entities.page_views.list({
    filter: { dateKey },
    limit: 1,
  })
  const existing = list?.[0] as {
    _id?: string
    views?: number
    homeViews?: number
    articleViews?: number
  } | undefined

  if (existing?._id) {
    const nextViews = (existing.views || 0) + amount
    await lumi.entities.page_views.update(existing._id, {
      views: nextViews,
      homeViews: (existing.homeViews || 0) + amount,
      updatedAt: nowIso,
    })
    return { dateKey, views: nextViews, added: amount }
  }

  await lumi.entities.page_views.create({
    dateKey,
    views: amount,
    homeViews: amount,
    articleViews: 0,
    creator: 'booster',
    createdAt: nowIso,
    updatedAt: nowIso,
  })
  return { dateKey, views: amount, added: amount }
}
